import { useApolloClient } from '@vue/apollo-composable'
import type { Ref } from 'vue'
import { watch } from 'vue'

import { getT } from '@/lib/ai/i18n'
import bus, { eventNames } from '@/lib/eventBus'
import { availableCountryCode } from '@/lib/onboardingCountry'
import COMPANY from '@/modules/company/queries/Company.gql'
import UPDATE_COMPANY_AMBASSADOR from '@/modules/company/queries/UpdateAmbassador.gql'
import UPDATE_COMPANY from '@/modules/company/queries/UpdateCompany.gql'
import UPDATE_COMPANY_CONTACT_PERSON from '@/modules/company/queries/UpdateCompanyContactPerson.gql'
import UPDATE_COMPANY_THEME from '@/modules/company/queries/UpdateCompanyTheme.gql'
import UPDATE_SLOGAN from '@/modules/company/queries/UpdateSlogan.gql'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { PendingAction } from '@/stores/aiAssistant/types'

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>

interface UseAiActionHandlerOptions {
  /** Reactive ref kept in sync with the Apollo company object from onboarding/index.vue */
  companyRef: Ref<CompanyData | null>
  /**
   * Moves the stepper to a numeric step value (1-based).
   * Called by the navigate_to_step handler.
   */
  setStep: (step: number) => void
  /**
   * Resolves a step route slug (e.g. 'publication') to its numeric stepper value.
   * Returns 1 as a safe fallback if the route is not found.
   */
  getStep: (route: string) => number
  /** Invoked after a confirmed mutation succeeds (form sync). */
  onConfirmedActionSuccess?: (action: PendingAction) => void
  /**
   * Snapshot of ambassador fields from the enter-data form so GraphQL never sends null.
   */
  getAmbassadorFormDraft?: () => Record<string, string> | null
}

/**
 * Watches for confirmed AI pending actions and dispatches them to the correct
 * GraphQL mutation handler. Once handled (success or error), clears pendingAction.
 *
 * Must be called from a component's setup() function so that useApolloClient()
 * can resolve the correct Apollo client instance.
 */
export function useAiActionHandler({
  companyRef,
  setStep,
  getStep,
  onConfirmedActionSuccess,
  getAmbassadorFormDraft,
}: UseAiActionHandlerOptions) {
  const aiStore = useAiAssistantStore()
  const { resolveClient } = useApolloClient()

  /** Re-fetches the full COMPANY query so all child step components re-render with fresh data. */
  const refetchCompany = (companyId: string) => [{ query: COMPANY, variables: { id: companyId } }]

  watch(
    () => aiStore.pendingAction,
    async (action) => {
      if (!action || action.status !== 'confirmed') return

      const t = getT()

      try {
        await dispatch(action)
        onConfirmedActionSuccess?.(action)

        // Only show snackbar for real mutations, not navigation
        if (action.mutation !== 'navigate_to_step') {
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'success',
            text: t('ai-assistant.responses.done', { label: action.label }),
          })
        }
      } catch (err) {
        console.error('[AI] Action handler failed:', err)
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: err instanceof Error ? err.message : String(err),
        })
      } finally {
        aiStore.pendingAction = null
      }
    },
    { deep: true }
  )

  async function dispatch(action: PendingAction): Promise<void> {
    const client = resolveClient()
    const c = companyRef.value

    switch (action.mutation) {
      case 'navigate_to_step': {
        const targetStep = getStep(String(action.proposedValue))
        setStep(targetStep)
        break
      }

      case 'updateCompanyTheme': {
        if (!c) throw new Error('[AI] Company data is not loaded yet.')

        const colorKey = action.field.split('.').pop() as 'primary' | 'secondary'
        if (!colorKey || !['primary', 'secondary'].includes(colorKey)) {
          throw new Error(`Unknown colour field: ${action.field}`)
        }

        const newValue = String(action.proposedValue)
        if (!HEX_PATTERN.test(newValue)) {
          throw new Error(
            `Invalid hex colour "${newValue}" — must be a 6-digit hex code like #FF5733.`
          )
        }

        const existingColors = c.theme?.colors ?? {}
        await client.mutate({
          mutation: UPDATE_COMPANY_THEME,
          variables: {
            input: {
              companyId: c.id,
              colors: {
                primary: existingColors.primary ?? '#000000',
                secondary: existingColors.secondary ?? '#000000',
                // Override only the colour that was confirmed
                [colorKey]: newValue,
              },
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompany': {
        if (!c) throw new Error('[AI] Company data is not loaded yet.')

        const links = { ...(c.links ?? {}) }
        const address = { ...(c.address ?? {}) }
        let name: string = c.name ?? ''
        const commission = c.commission ?? null
        let brokerageName = c.brokerageName ?? null
        let licenseNumber = c.licenseNumber ?? null

        // Apply the single confirmed field change without touching the rest
        switch (action.field) {
          case 'name':
            name = String(action.proposedValue)
            break
          case 'website':
            links.website = String(action.proposedValue)
            break
          case 'imprint':
            links.imprint = String(action.proposedValue)
            break
          case 'privacy':
            links.privacy = String(action.proposedValue)
            break
          case 'about':
            links.about = String(action.proposedValue)
            break
          case 'street':
            address.street = String(action.proposedValue)
            break
          case 'zip':
            address.zip = String(action.proposedValue)
            break
          case 'city':
            address.city = String(action.proposedValue)
            break
          case 'country':
            // `CountryCode` only accepts lowercase ISO-2 the platform offers.
            address.country = availableCountryCode(action.proposedValue, getT())
            break
          case 'state':
            address.state = String(action.proposedValue)
            break
          case 'brokerage_name':
          case 'brokerageName':
            brokerageName = String(action.proposedValue)
            break
          case 'license_number':
          case 'licenseNumber':
            licenseNumber = String(action.proposedValue)
            break
          default:
            console.warn('[AI] updateCompany: unhandled field:', action.field)
        }

        await client.mutate({
          mutation: UPDATE_COMPANY,
          variables: {
            input: {
              companyId: c.id,
              name,
              links: {
                website: links.website ?? null,
                imprint: links.imprint ?? null,
                about: links.about ?? null,
                privacy: links.privacy ?? null,
              },
              address: {
                street: address.street ?? null,
                zip: address.zip ?? null,
                city: address.city ?? null,
                country: address.country ?? null,
                state: address.state ?? null,
              },
              commission,
              brokerageName,
              licenseNumber,
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyContactPerson': {
        if (!c) throw new Error('[AI] Company data is not loaded yet.')

        const contactPersons: CompanyData[] = c.contactPersons ?? []
        const generalContact = contactPersons.find((p) => p.type === 'GENERAL') ?? {}

        if (!generalContact.id) {
          throw new Error('[AI] Could not find GENERAL contact person ID in company data.')
        }

        // action.field is e.g. 'contactPerson.email' — take the leaf segment
        const leafField = action.field.split('.').pop() as string

        await client.mutate({
          mutation: UPDATE_COMPANY_CONTACT_PERSON,
          variables: {
            input: {
              id: generalContact.id,
              companyId: c.id,
              type: 'GENERAL',
              companyName: generalContact.companyName ?? null,
              firstname: generalContact.firstname ?? null,
              lastname: generalContact.lastname ?? null,
              email: generalContact.email ?? null,
              phone: generalContact.phone ?? null,
              // Override the confirmed leaf field
              [leafField]: String(action.proposedValue),
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateAmbassador': {
        if (!c) throw new Error('[AI] Company data is not loaded yet.')

        const amb = c.ambassador ?? {}
        const draft = getAmbassadorFormDraft?.()
        const leafField = action.field.split('.').pop() as string

        await client.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR,
          variables: {
            input: {
              companyId: c.id,
              salutation: draft?.salutation ?? amb.salutation ?? null,
              firstname: draft?.firstname ?? amb.firstname ?? null,
              lastname: draft?.lastname ?? amb.lastname ?? null,
              role: draft?.role ?? amb.role ?? null,
              email: draft?.email ?? amb.email ?? null,
              phone: draft?.phone ?? amb.phone ?? null,
              mobile: draft?.mobile ?? amb.mobile ?? null,
              // Override the confirmed leaf field
              [leafField]: String(action.proposedValue),
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateSlogan': {
        if (!c) throw new Error('[AI] Company data is not loaded yet.')

        await client.mutate({
          mutation: UPDATE_SLOGAN,
          variables: {
            input: {
              companyId: c.id,
              slogan: String(action.proposedValue),
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      default:
        // TODO(wire): register a handler for this mutation in useAiActionHandler.ts
        console.warn(`[AI] No handler registered for mutation: "${action.mutation}"`)
    }
  }
}
